/**
 * Login/Register page
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './login.css';
import logo from '../assets/logo.svg';
import globeVideo from '../assets/globe-movie-1280x720.mp4';

export default function Login() {
    const [loginType, setLoginType] = useState<'login' | 'register'>('login');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [usernameAvailability, setUsernameAvailability] = useState<{
        checking: boolean;
        available: boolean | null;
        message: string;
    }>({ checking: false, available: null, message: '' });
    const { login, register } = useAuth();
    const navigate = useNavigate();
    const usernameCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Check username availability with debounce
    useEffect(() => {
        // Only check username availability in register mode
        if (loginType !== 'register') {
            setUsernameAvailability({
                checking: false,
                available: null,
                message: '',
            });
            return;
        }

        // Clear previous timeout
        if (usernameCheckTimeoutRef.current) {
            clearTimeout(usernameCheckTimeoutRef.current);
        }

        // Don't check if username is empty or too short
        if (!username || username.trim().length < 3) {
            setUsernameAvailability({
                checking: false,
                available: null,
                message: '',
            });
            return;
        }

        // Set checking state
        setUsernameAvailability({
            checking: true,
            available: null,
            message: '',
        });

        // Debounce the API call (1.5 seconds)
        usernameCheckTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await fetch('/api/auth/check-username', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ username: username.trim() }),
                });

                const data = await response.json();

                if (data.available) {
                    setUsernameAvailability({
                        checking: false,
                        available: true,
                        message: 'Username is available',
                    });
                } else {
                    setUsernameAvailability({
                        checking: false,
                        available: false,
                        message: data.error || 'Username is not available',
                    });
                }
            } catch (err) {
                setUsernameAvailability({
                    checking: false,
                    available: null,
                    message: 'Error checking username',
                });
            }
        }, 1500);

        // Cleanup timeout on unmount
        return () => {
            if (usernameCheckTimeoutRef.current) {
                clearTimeout(usernameCheckTimeoutRef.current);
            }
        };
    }, [username, loginType]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result =
            loginType === 'login'
                ? await login(username, password)
                : await register(username, password, email);

        setLoading(false);

        if (result.success) {
            navigate('/');
        } else {
            setError(result.error || 'An error occurred');
        }
    };

    // Check if form is valid based on login type
    const isFormValid =
        loginType === 'login'
            ? username.trim().length > 0 && password.trim().length > 0
            : username.trim().length > 0 &&
              password.trim().length > 0 &&
              email.trim().length > 0 &&
              usernameAvailability.available === true;

    return (
        <div className="loginMain">
            <video
                className="loginBackgroundVideo"
                autoPlay
                loop
                muted
                playsInline
            >
                <source src={globeVideo} type="video/mp4" />
            </video>
            <div className="loginTagline">
                A world full of trivia awaits. Do you have what it takes to race
                to the top?
            </div>
            <div className="loginTagline2">
                Thousands of quizzes. Endless fun. Free.
            </div>
            <div className="loginMainPanel">
                <div className="loginHeader">
                    <h1 className="appTitle">
                        <img
                            src={logo}
                            alt="Trivia Logo"
                            className="loginLogo"
                        />
                        Trivia
                    </h1>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="loginFieldset">
                        <label
                            className={`loginRadioLabel ${
                                loginType === 'login'
                                    ? 'loginRadioLabelChecked'
                                    : ''
                            }`}
                        >
                            <input
                                type="radio"
                                className="loginRadio"
                                name="loginType"
                                value="login"
                                checked={loginType === 'login'}
                                onChange={() => setLoginType('login')}
                            />{' '}
                            Login
                        </label>
                        <span className="loginOr">or</span>
                        <label
                            className={`loginRadioLabel ${
                                loginType === 'register'
                                    ? 'loginRadioLabelChecked'
                                    : ''
                            }`}
                        >
                            <input
                                className="loginRadio"
                                type="radio"
                                name="loginType"
                                value="register"
                                checked={loginType === 'register'}
                                onChange={() => setLoginType('register')}
                            />{' '}
                            Register
                        </label>
                    </div>

                    <div>
                        <div className="formInputContainer">
                            <label htmlFor="username-input">Username</label>
                            <input
                                id="username-input"
                                name="username"
                                className="formInput"
                                placeholder="your-username"
                                autoComplete="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                            />
                            {loginType === 'register' &&
                                username.trim().length >= 3 && (
                                    <p
                                        style={{
                                            marginTop: '0.5rem',
                                            fontSize: '0.875rem',
                                            color: usernameAvailability.checking
                                                ? '#666'
                                                : usernameAvailability.available ===
                                                  true
                                                ? '#22c55e'
                                                : usernameAvailability.available ===
                                                  false
                                                ? '#ef4444'
                                                : '#666',
                                        }}
                                    >
                                        {usernameAvailability.checking
                                            ? 'Checking availability...'
                                            : usernameAvailability.message}
                                    </p>
                                )}
                        </div>
                        {loginType === 'register' && (
                            <div className="formInputContainer">
                                <label htmlFor="email-input">Email</label>
                                <input
                                    id="email-input"
                                    name="email"
                                    className="formInput"
                                    type="email"
                                    placeholder="your-email@domain.com"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        )}
                        <div className="formInputContainer">
                            <label htmlFor="password-input">Password</label>
                            <input
                                id="password-input"
                                name="password"
                                className="formInput"
                                type="password"
                                placeholder="twixrox"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>

                        <button
                            className="formButton"
                            type="submit"
                            disabled={loading || !isFormValid}
                        >
                            {loading
                                ? 'Please wait...'
                                : loginType === 'login'
                                ? 'Login'
                                : 'Register'}
                        </button>
                        {error && (
                            <p
                                style={{ color: 'red' }}
                                role="alert"
                                className="errorMessage"
                            >
                                {error}
                            </p>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
